import { supabase } from './auth.service';
import { invokeEdgeFunction } from '../lib/edge-function';
import {
  AppNotification,
  NotificationListResponse,
  NotificationType,
} from '../entities/notification.entity';
import type { NotificationPreferences } from '../entities/notification-preferences.entity';

const DEFAULT_PREFERENCES: NotificationPreferences = {
  soundEnabled: true,
  soundPriorityFilter: ['high'],
  minSoundIntervalSecs: 30,
  quietHoursStart: null,
  quietHoursEnd: null,
  disabledTypes: [],
};

function getFunctionsUrl(): string {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
  return `${supabaseUrl}/functions/v1`;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

class NotificationService {
  private async getUserAndAccountId(): Promise<{
    userId: string;
    accountId: string;
  }> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const accountId = user.user_metadata?.account_id;
    if (accountId) return { userId: user.id, accountId };

    const { data } = await supabase.from('users').select('account_id').eq('id', user.id).single();
    if (!data?.account_id) throw new Error('No account found');
    return { userId: user.id, accountId: data.account_id };
  }

  private mapPreferencesToEntity(row: Record<string, unknown>): NotificationPreferences {
    return {
      soundEnabled: row.sound_enabled as boolean,
      soundPriorityFilter: (row.sound_priority_filter as string[]) ?? DEFAULT_PREFERENCES.soundPriorityFilter,
      minSoundIntervalSecs: (row.min_sound_interval_secs as number) ?? DEFAULT_PREFERENCES.minSoundIntervalSecs,
      quietHoursStart: (row.quiet_hours_start as string | null) ?? null,
      quietHoursEnd: (row.quiet_hours_end as string | null) ?? null,
      disabledTypes: (row.disabled_types as string[]) ?? DEFAULT_PREFERENCES.disabledTypes,
    };
  }

  async getNotifications(params?: {
    page?: number;
    limit?: number;
    unreadOnly?: boolean;
    type?: NotificationType;
  }): Promise<NotificationListResponse> {
    const url = new URL(`${getFunctionsUrl()}/get-notifications`);
    if (params?.page) url.searchParams.set('page', String(params.page));
    if (params?.limit) url.searchParams.set('limit', String(params.limit));
    if (params?.unreadOnly) url.searchParams.set('unreadOnly', 'true');
    if (params?.type) url.searchParams.set('type', params.type);

    const headers = await getAuthHeaders();
    const response = await fetch(url.toString(), { headers });

    if (!response.ok) {
      throw new Error(`Failed to fetch notifications: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data as NotificationListResponse;
  }

  async getUnreadCount(): Promise<number> {
    const result = await this.getNotifications({ limit: 1 });
    return result.unreadCount;
  }

  async markAsRead(notificationIds: string[]): Promise<void> {
    await invokeEdgeFunction('mark-notifications-read', {
      body: { notificationIds },
    });
  }

  async markAllAsRead(): Promise<void> {
    await invokeEdgeFunction('mark-notifications-read', {
      body: { all: true },
    });
  }

  async getPreferences(): Promise<NotificationPreferences> {
    const { userId } = await this.getUserAndAccountId();

    const { data, error } = await supabase
      .from('notification_preferences')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw new Error(`Failed to fetch notification preferences: ${error.message}`);
    if (!data) return { ...DEFAULT_PREFERENCES };

    return this.mapPreferencesToEntity(data as Record<string, unknown>);
  }

  async updatePreferences(
    prefs: Partial<NotificationPreferences> & { resetQuietHours?: boolean }
  ): Promise<NotificationPreferences> {
    const { userId, accountId } = await this.getUserAndAccountId();

    const row: Record<string, unknown> = {
      user_id: userId,
      account_id: accountId,
    };

    if (prefs.soundEnabled !== undefined) row.sound_enabled = prefs.soundEnabled;
    if (prefs.soundPriorityFilter !== undefined) row.sound_priority_filter = prefs.soundPriorityFilter;
    if (prefs.minSoundIntervalSecs !== undefined) row.min_sound_interval_secs = prefs.minSoundIntervalSecs;
    if (prefs.disabledTypes !== undefined) row.disabled_types = prefs.disabledTypes;

    if (prefs.resetQuietHours) {
      row.quiet_hours_start = null;
      row.quiet_hours_end = null;
    } else {
      if (prefs.quietHoursStart !== undefined) row.quiet_hours_start = prefs.quietHoursStart;
      if (prefs.quietHoursEnd !== undefined) row.quiet_hours_end = prefs.quietHoursEnd;
    }

    const { error } = await supabase
      .from('notification_preferences')
      .upsert(row, { onConflict: 'user_id,account_id' });

    if (error) throw new Error(`Failed to update notification preferences: ${error.message}`);

    return this.getPreferences();
  }

  mapToEntity(row: Record<string, unknown>): AppNotification {
    return {
      id: row.id as string,
      type: row.type as AppNotification['type'],
      title: row.title as string,
      message: row.message as string,
      priority: row.priority as AppNotification['priority'],
      entityType: row.entity_type as string | null,
      entityId: row.entity_id as string | null,
      metadata: (row.metadata as Record<string, unknown>) || {},
      isRead: row.is_read as boolean,
      userId: row.user_id as string | null,
      createdAt: row.created_at as string,
    };
  }
}

export const notificationService = new NotificationService();
